import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setAlert } from "../store/slices/alertSlice";
import { searchUsers } from "../services/api";
import UserPickerOption from "./UserPickerOption";
import DismissibleWorkflowAlert from "./DismissibleWorkflowAlert";
import {
  formatUserOptionLabel,
} from "../utils/userDisplay";

const HODSelection = ({ data, onChange, disabled = false }) => {

  const dispatch = useDispatch();

  const [hodSearch, setHodSearch] = useState("");
  const [hodList, setHodList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);

  /* ---------------- SEARCH HOD ---------------- */
  const handleSearch = async (e) => {

    const text = e.target.value;
    setHodSearch(text);

    if (!text) {
      setShowDropdown(false);
      setHodList([]);
      return;
    }

    try {
      const res = await searchUsers(text);
      
      // 🔥 FILTER ONLY HOD ROLE
      const hodUsers = res.data.users.filter(u => u.role?.roleName === "Supervisor/HOD");

      setHodList(hodUsers);
      setShowDropdown(true);

    } catch (err) {
      dispatch(setAlert({ message: "Error loading HODs", type: "danger" }));
    }
  };

  /* ---------------- SELECT HOD ---------------- */
  const selectHod = (user) => {

    const selected = data.hodObjects || [];

    const exists = selected.find(u => u._id === user._id);

    let updated;

    if (exists) {
      updated = selected.filter(u => u._id !== user._id);
    } else {
      updated = [...selected, user];
    }

    onChange({
      ...data,
      hodIds: updated.map(u => u._id),   // ✅ backend
      hodObjects: updated               // ✅ UI
    });

    setHodSearch("");
    setShowDropdown(false);
  };

  /* ---------------- PRIMARY SELECT ---------------- */
  const handlePrimaryChange = (e) => {

    onChange({
      ...data,
      primaryHodId: e.target.value
    });
  };

  /* ---------------- CLOSE DROPDOWN ---------------- */
  useEffect(() => {

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };

  }, []);

  const showGuidance = !disabled;

  return (
    <div className="form-container compact">

      {showGuidance && (
      <DismissibleWorkflowAlert variant="info" className="workflow-info-alert mb-3">
        <strong>HOD assignment is separate from user comments.</strong>
        <span className="d-block mt-1 small mb-0">
          Select <em>Supervisor/HOD</em> users who will review this incident. This step is available after investigation is
          completed. If user comments were requested earlier, all assigned users must reply before this section can be submitted.
        </span>
      </DismissibleWorkflowAlert>
      )}

      {/* ================= HOD SEARCH ================= */}
      <div className="form-row">
        <div className="form-control" ref={dropdownRef} style={{ position: "relative" }}>

          <label>Select HOD(s)</label>

          <input
            type="text"
            placeholder="Search HOD..."
            value={hodSearch}
            onChange={handleSearch}
            className="form-control"
            disabled={disabled}
          />

          {!disabled && showDropdown && hodList.length > 0 && (
            <div className="dropdown-list">

              {hodList.map(user => {

                const isSelected = data.hodObjects?.some(u => u._id === user._id);

                return (
                  <div
                    key={user._id}
                    className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectHod(user)}
                  >
                    <span className="d-flex justify-content-between align-items-start gap-2">
                      <UserPickerOption user={user} />
                      {isSelected && <span>✔</span>}
                    </span>
                  </div>
                );
              })}

            </div>
          )}

          {/* Selected HODs */}
          {data.hodObjects?.length > 0 && (
            <div className="mt-2">
              {data.hodObjects.map(u => {
                const label = formatUserOptionLabel(u);

                return (
                <span
                  key={u._id}
                  className="badge bg-primary me-2 mb-1"
                  style={{ cursor: disabled ? "default" : "pointer" }}
                  onClick={() => {
                    if (!disabled) selectHod(u);
                  }}
                  title={label}
                >
                  {label} ✕
                </span>
              )})}
            </div>
          )}

        </div>
      </div>

      {/* ================= PRIMARY HOD ================= */}
      {data.hodObjects?.length > 0 && (
        <div className="form-row">
          <div className="form-control">

            <label>Primary HOD</label>

            <select
              value={data.primaryHodId || ""}
              onChange={handlePrimaryChange}
              disabled={disabled}
            >
              <option value="">Select Primary HOD</option>

              {data.hodObjects.map(u => (
                <option key={u._id} value={u._id}>
                  {formatUserOptionLabel(u)}
                </option>
              ))}

            </select>

          </div>
        </div>
      )}

    </div>
  );
};

export default HODSelection;

